"use client";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  IconButton,
  Divider,
  Fade,
  useTheme,
  useMediaQuery,
  Alert,
} from "@mui/material";

import {
  Close as CloseIcon,
  Security as SecurityIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import { useLoggedIn } from "@/store/useAuthStore";
import { isUnder21 } from "@/lib/isUnder21";
import CustomNavLink from "../CustomLink/CustomNavLink";
import { useDictionary } from "../DictProvider/DictProvider";

const AgeConsent = ({ open, onClose, onConfirm }) => {
  const dict = useDictionary();
  const { auth, profile } = useLoggedIn();
  const [isChecked, setIsChecked] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleConfirm = () => {
    if (isChecked) {
      onConfirm();
    }
  };

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: "#ffffff",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          border: "1px solid rgba(0, 0, 0, 0.08)",
        },
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      <Box sx={{ position: "relative" }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "rgba(0, 0, 0, 0.6)",
            "&:hover": {
              color: "rgba(0, 0, 0, 0.8)",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            {auth && (!profile?.DOB || isUnder21(profile.DOB)) ? (
              <CancelIcon
                sx={{
                  fontSize: 48,
                  color: "red",
                  mb: 2,
                }}
              />
            ) : (
              <SecurityIcon
                sx={{
                  fontSize: 48,
                  color: "#ac7529",
                  mb: 2,
                }}
              />
            )}
            {auth && (!profile?.DOB || isUnder21(profile.DOB)) ? (
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: "#1a1a1a",
                  fontSize: isMobile ? "1.25rem" : "1.5rem",
                }}
              >
                {dict?.ageConsent?.restrictedItemLabel}
              </Typography>
            ) : (
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: "#1a1a1a",
                  fontSize: isMobile ? "1.25rem" : "1.5rem",
                }}
              >
                {dict?.ageConsent?.verificationTitle}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {auth && (!profile?.DOB || isUnder21(profile.DOB)) ? null : (
            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 1,
                p: 2,
                mb: 2,
                border: "1px solid #e9ecef",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.7,
                  color: "#495057",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                }}
              >
                {dict?.ageConsent?.verificationNote}
              </Typography>
            </Box>
          )}

          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{
              mb: 2,
              backgroundColor: "#fff3cd",
              color: "#856404",
              "& .MuiAlert-icon": {
                color: "#856404",
              },
            }}
          >
            {auth && (!profile?.DOB || isUnder21(profile.DOB)) ? (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {dict?.ageConsent?.accessWarning}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {dict?.ageConsent?.deliveryPolicy}
              </Typography>
            )}
          </Alert>

          {auth && (!profile?.DOB || isUnder21(profile.DOB)) ? null : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  sx={{
                    color: "#6c757d",
                    "&.Mui-checked": {
                      color: "#ac7529",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isMobile ? "0.9rem" : "1rem",
                    fontWeight: 500,
                    color: "#495057",
                  }}
                >
                  {dict?.ageConsent?.confirmIntro} {" "}
                  <CustomNavLink
                    href="/age-verification"
                    className="cursor-pointer text-primary/90"
                  >
                    {dict?.ageConsent?.confirmRead}
                  </CustomNavLink>{" "}
                  {dict?.ageConsent?.confirmEnd}
                </Typography>
              }
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{ px: 4, pb: 3, gap: 2, justifyContent: "flex-end" }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#dee2e6",
              "&:hover": {
                borderColor: "#adb5bd",
                backgroundColor: "#f8f9fa",
              },
              borderRadius: 1,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {dict?.cancellationPage?.cancel}
          </Button>
          {auth && (!profile?.DOB || isUnder21(profile.DOB)) ? null : (
            <Button
              onClick={handleConfirm}
              disabled={!isChecked}
              variant="contained"
              sx={{
                backgroundColor: isChecked ? "#ac7529" : "#e9ecef",
                color: isChecked ? "#ffffff" : "#6c757d",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: isChecked ? "#ac7529" : "#e9ecef",
                },
                "&:disabled": {
                  backgroundColor: "#e9ecef",
                  color: "#6c757d",
                },
                borderRadius: 1,
                px: 4,
                py: 1.5,
                textTransform: "none",
                boxShadow: isChecked
                  ? "0 2px 8px rgba(25, 118, 210, 0.3)"
                  : "none",
              }}
            >
              {dict?.ageConsent?.confirmButton}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AgeConsent;
